import type { DraftWarning, RecipeDraft, RecipeWriteInput } from '@recipes/contracts';
import { draftToRecipeInput, findPreferenceConflicts } from '@recipes/domain';
import {
  Alert,
  Badge,
  Button,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@recipes/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FeedbackButtons } from '@/features/ai/feedback-buttons';
import { ScanPictureSection, type ScanPicture } from '@/features/ai/scan-picture';
import { VideoThumbnailSection } from '@/features/ai/video-thumbnail';
import {
  loadGenerationRequest,
  storeGenerationRequest,
  type StoredGenerationRequest,
} from '@/features/ai/generation-storage';
import { QuestionsForm } from '@/features/ai/questions-form';
import { useGeneration } from '@/features/ai/use-generation';
import { DraftReviewForm } from '@/features/imports/draft-review-form';
import { WarningsList } from '@/features/imports/review-parts';
import { useProfile } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { api } from '@/lib/api';
import { describeError, describeWorkerError, toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

export function AiReviewPage() {
  const { generationId = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const gen = useGeneration(generationId || null);
  const stored = useMemo(() => loadGenerationRequest(generationId), [generationId]);

  const [draftIndex, setDraftIndex] = useState(0);
  const [questionsSkipped, setQuestionsSkipped] = useState(false);
  const [ocrText, setOcrText] = useState<string | null>(null);

  const resubmit = useMutation({
    mutationFn: async (input: { answers?: Record<string, string>; ocrText?: string }) => {
      if (input.ocrText !== undefined) {
        const body = {
          text: input.ocrText,
          mode: 'notes' as const,
          constraints: stored?.kind === 'video' ? undefined : stored?.body.constraints,
        };
        const next = await api.ai.recipeFromText(body, crypto.randomUUID());
        return { next, request: { kind: 'text', body } satisfies StoredGenerationRequest };
      }
      if (!stored) throw new Error(t('ai.review.noStoredRequest'));
      if (stored.kind === 'text') {
        const body = { ...stored.body, answers: input.answers, previousGenerationId: generationId };
        const next = await api.ai.recipeFromText(body, crypto.randomUUID());
        return { next, request: { kind: 'text', body } satisfies StoredGenerationRequest };
      }
      if (stored.kind === 'video') {
        const body = { ...stored.body, answers: input.answers, previousGenerationId: generationId };
        const next = await api.ai.recipeFromVideo(body, crypto.randomUUID());
        return { next, request: { kind: 'video', body } satisfies StoredGenerationRequest };
      }
      const body = { ...stored.body, answers: input.answers, previousGenerationId: generationId };
      const next = await api.ai.recipeFromImages(body, crypto.randomUUID());
      return { next, request: { kind: 'images', body } satisfies StoredGenerationRequest };
    },
    onSuccess: ({ next, request }) => {
      storeGenerationRequest(next.id, request);
      setQuestionsSkipped(false);
      setDraftIndex(0);
      navigate(`/add/ai/${next.id}`);
    },
    onError: (e) => toastError(e),
  });

  const [picture, setPicture] = useState<ScanPicture | null>(null);
  const save = useMutation({
    mutationFn: (input: RecipeWriteInput) => {
      // TODO(contract): POST /recipes has no field for aiGenerationId/origin (RecipesService.create
      // accepts them server-side only). Until the contract exposes it we keep an attribution line.
      const attribution = t('ai.review.attributionNote', { id: generationId });
      const notes = input.notes ? `${input.notes}\n\n${attribution}` : attribution;
      // The picture kept from the scan (a permanent copy) becomes the recipe's cover.
      const coverAssetId = picture?.assetId ?? input.coverAssetId;
      // A recipe written down from a video keeps the video (embedded on the recipe page).
      const videoUrl =
        input.videoUrl ?? (gen.parsed?.kind === 'video' ? gen.parsed.result.videoUrl : null);
      return api.recipes.create({ ...input, notes, coverAssetId, videoUrl }, crypto.randomUUID());
    },
    onSuccess: (recipe) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      toast.success(t('ai.review.saved'));
      navigate(`/recipes/${recipe.id}`);
    },
    onError: (e) => toastError(e, t('ai.review.saveFailed')),
  });

  if (gen.isLoading) {
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  }
  if (gen.error || !gen.generation) {
    return (
      <PageContainer>
        <Alert variant="destructive" title={t('ai.review.loadFailed')}>
          {describeError(gen.error)}
        </Alert>
      </PageContainer>
    );
  }
  const generation = gen.generation;
  const editHref =
    stored?.kind === 'images'
      ? '/add/images'
      : stored?.kind === 'video' || generation.capability === 'RECIPE_FROM_VIDEO'
        ? `/add/video?restore=${generationId}`
        : `/add/text?restore=${generationId}`;

  if (generation.status === 'QUEUED' || generation.status === 'RUNNING') {
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('ai.review.generating')} backTo="/add" />
        <ProcessingIndicator
          label={t(`ai.status.${generation.status}`)}
          progress={generation.status === 'RUNNING' ? gen.progress : null}
          detail={
            <>
              {generation.status === 'RUNNING' && `${gen.progress}%`}
              {gen.polling && ` · ${t('imports.job.polling')}`}
            </>
          }
        >
          {gen.questions && gen.questions.length > 0 && (
            <p className="mt-2 text-sm">{t('ai.review.questionsIncoming')}</p>
          )}
        </ProcessingIndicator>
      </PageContainer>
    );
  }

  if (generation.status === 'FAILED' || generation.status === 'CANCELLED') {
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('ai.review.title')} backTo="/add" />
        <Alert variant="destructive" title={t(`ai.status.${generation.status}`)}>
          <p>
            {describeWorkerError(
              gen.streamError?.code ?? generation.errorCode,
              gen.streamError?.message ?? generation.error,
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t('ai.inputKept')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {stored && (
              <Button size="sm" loading={resubmit.isPending} onClick={() => resubmit.mutate({})}>
                {t('app.retry')}
              </Button>
            )}
            <Button asChild size="sm" variant="outline">
              <Link to={editHref}>{t('ai.review.editInput')}</Link>
            </Button>
          </div>
        </Alert>
      </PageContainer>
    );
  }

  const parsed = gen.parsed;
  if (!parsed || parsed.result.drafts.length === 0) {
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('ai.review.title')} backTo="/add" />
        <Alert variant="warning" title={t('ai.unexpectedResult')}>
          <Button asChild size="sm" variant="outline" className="mt-2">
            <Link to={editHref}>{t('ai.review.editInput')}</Link>
          </Button>
        </Alert>
      </PageContainer>
    );
  }

  const drafts = parsed.result.drafts;
  const draft = drafts[Math.min(draftIndex, drafts.length - 1)] as RecipeDraft;
  const imageResult = parsed.kind === 'images' ? parsed.result : null;
  const videoResult = parsed.kind === 'video' ? parsed.result : null;
  const effectiveOcr = ocrText ?? imageResult?.ocrText ?? '';
  const showQuestions = draft.followUpQuestions.length > 0 && !questionsSkipped;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('ai.review.title')}
        description={t('ai.review.description')}
        backTo="/add"
        actions={
          <span className="text-xs text-muted-foreground">
            {generation.provider &&
              `${generation.provider}${generation.model ? ` · ${generation.model}` : ''} · `}
            {t('ai.review.promptVersion', { version: generation.promptVersion })}
          </span>
        }
      />

      {imageResult && (
        <section className="mb-6 space-y-3" aria-label={t('ai.images.resultSection')}>
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span>{t('ai.images.detected')}</span>
            <Badge variant="info">{t(`ai.images.kinds.${imageResult.inputKind}`)}</Badge>
          </p>
          {imageResult.inputKind === 'DISH_PHOTO' && (
            <Alert variant="warning" title={t('ai.images.estimateTitle')}>
              {t('ai.images.estimateBody')}
            </Alert>
          )}
          {(imageResult.inputKind === 'RECIPE_DOCUMENT' || imageResult.ocrText) && (
            <div className="space-y-2 rounded-lg border p-3">
              <label htmlFor="ocr-text" className="block text-sm font-medium">
                {t('ai.images.ocrTitle')}
              </label>
              <p className="text-xs text-muted-foreground">{t('ai.images.ocrHint')}</p>
              <Textarea
                id="ocr-text"
                rows={8}
                value={effectiveOcr}
                onChange={(e) => setOcrText(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={effectiveOcr.trim().length < 3}
                loading={resubmit.isPending && resubmit.variables?.ocrText !== undefined}
                onClick={() => resubmit.mutate({ ocrText: effectiveOcr.trim() })}
              >
                {t('ai.images.rerunFromText')}
              </Button>
            </div>
          )}
        </section>
      )}

      {videoResult && (
        <p className="mb-6 text-sm">
          {t('ai.video.source')}{' '}
          <a
            href={videoResult.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-primary underline underline-offset-2"
          >
            {videoResult.videoUrl}
          </a>
          <span className="mt-1 block text-xs text-muted-foreground">
            {t('ai.video.linkedHint')}
          </span>
        </p>
      )}

      {drafts.length > 1 && (
        <Tabs
          value={String(draftIndex)}
          onValueChange={(v) => setDraftIndex(Number(v))}
          className="mb-4"
        >
          <TabsList aria-label={t('ai.review.variants')}>
            {drafts.map((d, i) => (
              <TabsTrigger key={i} value={String(i)}>
                {videoResult
                  ? t('ai.video.dish', { index: i + 1 })
                  : t('ai.review.variant', { index: i + 1 })}
              </TabsTrigger>
            ))}
          </TabsList>
          {drafts.map((d, i) => (
            <TabsContent key={i} value={String(i)}>
              <p className="text-sm text-muted-foreground">{d.title}</p>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {showQuestions ? (
        <QuestionsForm
          questions={draft.followUpQuestions}
          submitting={resubmit.isPending}
          onSubmit={(answers) => resubmit.mutate({ answers })}
          onSkip={() => setQuestionsSkipped(true)}
        />
      ) : (
        <>
          {videoResult && (
            <div className="mb-6">
              <VideoThumbnailSection
                videoUrl={videoResult.videoUrl}
                value={picture}
                onChange={setPicture}
              />
            </div>
          )}
          {stored?.kind === 'images' && (
            <div className="mb-6">
              <ScanPictureSection
                sourceAssetIds={stored.body.assetIds}
                value={picture}
                onChange={setPicture}
              />
            </div>
          )}
          <DraftReview
            key={`${generationId}-${draftIndex}`}
            draft={draft}
            generationId={generationId}
            feedback={generation.feedback}
            profile={profile ?? null}
            saving={save.isPending}
            onSave={(input) => save.mutate(input)}
            editHref={editHref}
          />
        </>
      )}
    </PageContainer>
  );
}

function DraftReview({
  draft,
  generationId,
  feedback,
  profile,
  saving,
  onSave,
  editHref,
}: {
  draft: RecipeDraft;
  generationId: string;
  feedback: 'UP' | 'DOWN' | null;
  profile: {
    dietaryPreferences: RecipeDraft['dietaryLabels'];
    avoidedIngredients: string[];
    defaultServings: number;
  } | null;
  saving: boolean;
  onSave: (input: RecipeWriteInput) => void;
  editHref: string;
}) {
  const { t } = useTranslation();
  const initial = useMemo(
    () =>
      draftToRecipeInput(draft, {
        defaultServings: profile ? String(profile.defaultServings) : undefined,
      }),
    [draft, profile],
  );
  const warnings = useMemo<DraftWarning[]>(() => {
    const list = [...draft.warnings];
    if (profile) {
      for (const c of findPreferenceConflicts(
        draft.ingredients.map((i) => i.name),
        profile,
      )) {
        const message = t('review.preferenceConflict', {
          label: c.label.replace(/_/g, ' '),
          ingredients: c.ingredients.join(', '),
        });
        if (!list.some((w) => w.code === 'CONFLICTS_WITH_PREFERENCES' && w.message === message))
          list.push({ code: 'CONFLICTS_WITH_PREFERENCES', severity: 'warning', message });
      }
    }
    if (
      currentLocale() &&
      draft.ingredients.some((i) => i.confidence !== 'high') &&
      !list.some((w) => w.code === 'ESTIMATED_QUANTITIES')
    ) {
      list.push({
        code: 'ESTIMATED_QUANTITIES',
        severity: 'info',
        message: t('ai.review.estimatedHint'),
      });
    }
    return list;
  }, [draft, profile, t]);

  return (
    <div className="space-y-6">
      <Alert variant="info" title={t('review.badgeAi')}>
        {t('ai.review.generatedNotice')}
      </Alert>
      <WarningsList warnings={warnings} uncertainties={draft.uncertainties} />
      <FeedbackButtons generationId={generationId} initial={feedback} />
      <DraftReviewForm
        initial={initial}
        draft={draft}
        origin="AI"
        submitLabel={t('ai.review.save')}
        submitting={saving}
        onSubmit={onSave}
        secondaryActions={
          <Button asChild type="button" variant="outline" size="lg">
            <Link to={editHref}>{t('ai.review.editInput')}</Link>
          </Button>
        }
      />
    </div>
  );
}
